import { AloftPrice } from '@aloft/toolkit-shared';

import { Td } from '../../../components/pdf-export/Td/Td.tsx';
import { Text } from '../../../components/pdf-export/Text/Text.tsx';
import { Tr } from '../../../components/pdf-export/Tr/Tr.tsx';
import { MonthGrouping } from '../../marketConditions/zillow/types.ts';
import { SummaryTable } from '../SummaryTable.tsx';
import { SummaryTableExtraCells } from '../SummaryTableExtraCells.tsx';
import { Tkv2PairedCostSummaryInterfaceV2 } from '../types.ts';
import {
  MONTHS_PER_TABLE,
  chunkFilteredHousingTrendsData,
} from '../utils/chunkFilteredHousingTrendsData.ts';

const FIRST_COLUMN_WIDTH = '22%';
const OTHER_COLUMN_WIDTH = '13%';

const rows: {
  labelFunc: (props: Tkv2PairedCostSummaryInterfaceV2) => string;
  valueFunc: (val?: unknown) => string;
  key: keyof MonthGrouping;
}[] = [
  {
    key: 'typicalValue',
    labelFunc: (props: Tkv2PairedCostSummaryInterfaceV2) => {
      if (props.marketConditions.dateOfSaleAdjustment.selectedMethod === 'customAvgPricePerMonth') {
        return 'Custom Typical Home Value';
      }

      return props.housingTrends?.filteredHousingTrendsData.isZillow
        ? `Zillow Typical Home Value Zip Code ${props.housingTrends?.postalCode}`
        : `MLS Median Close Price Zip Code ${props.housingTrends?.postalCode}`;
    },
    valueFunc: (val) => (val as AloftPrice)?.toString(true) ?? '',
  },
  {
    key: 'percentChange',
    labelFunc: () => 'Date of Sale Adj.',
    valueFunc: (val) => `${(val as number).toFixed(2)}%`,
  },
  {
    key: 'affectedComps',
    labelFunc: () => 'Comps Affected',
    valueFunc: (val) => (val as string | undefined) ?? '',
  },
];

export const MarketConditionsAdjustmentExportTable = (props: Tkv2PairedCostSummaryInterfaceV2) => {
  const filteredHousingTrendsData = props.housingTrends?.filteredHousingTrendsData;
  const chunks = chunkFilteredHousingTrendsData(filteredHousingTrendsData, props.marketConditions);

  return chunks.map((chunk, idx) => (
    <SummaryTable key={`table-${idx}`}>
      <Tr>
        <Td border="0 !important" width={FIRST_COLUMN_WIDTH}></Td>
        {chunk.map((data) => (
          <Td key={`month-header-${data.month}`} border="0 !important" width={OTHER_COLUMN_WIDTH}>
            <Text variant="h5">{data.month}</Text>
          </Td>
        ))}
        <SummaryTableExtraCells
          count={chunk.length - MONTHS_PER_TABLE}
          span={1}
          suppressBorder={true}
          isFirstRow={false}
        />
      </Tr>
      {rows.map(({ key, labelFunc, valueFunc }, idx) => (
        <Tr key={`${key}-row`}>
          <Td isFirstColumn isFirstRow={idx === 0} width={FIRST_COLUMN_WIDTH}>
            <Text variant="body1">{labelFunc(props)}</Text>
          </Td>
          {chunk.map((data) => (
            <Td
              key={`typical-value-${data.month}`}
              isFirstRow={idx === 0}
              width={OTHER_COLUMN_WIDTH}
            >
              <Text variant="body1" underlined={data.type === 'projection'}>
                {valueFunc(data[key])}
              </Text>
            </Td>
          ))}
          <SummaryTableExtraCells
            count={chunk.length - MONTHS_PER_TABLE}
            span={1}
            suppressBorder={false}
            isFirstRow={idx === 0}
          />
        </Tr>
      ))}
    </SummaryTable>
  ));
};
